import FileUpload from 'components/hoc/FileUpload';
import Upload from 'components/Icons/Upload';
import BaseInput from 'components/inputs/BaseInput';
import { SelectOption } from 'components/inputs/BaseSelect';
import BaseTextarea from 'components/inputs/BaseTextarea';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import OrderedSetMultiSelect from 'components/inputs/dropdowns/OrderedSetMultiSelect';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import { IconButton } from 'components/lib';
import { InsertFilesSubmitParams } from 'components/modals/InsertFilesModal/InsertFilesModal';
import OverlaySection from 'components/Overlays/OverlaySection';
import { useIsPendoEnabled } from 'hooks/useIsPendoEnabled';
import { OrderedSet } from 'immutable';
import React from 'react';
import { ImpactCategory, RFIImpactCategoryKind } from 'records/ImpactCategory';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { ImpactCategories, RFI } from 'records/RFI';
import { RFIAttachment } from 'records/RFIAttachment';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { useTranslator } from '@bluebeam/react-i18n';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import InsertFilesButton from '../InsertFilesButton';
import RFIFilesList from '../RFIFilesList';
import { useViewRfiMachine, ViewRFIEventsEnum } from './ViewRFIMachine';
import { AttachmentTypeEnum } from './ViewRFIMachine/viewRfiMachineTypes';

const OfficialQuestionSection: React.FC = () => {
  const { isPendoEnabled } = useIsPendoEnabled();
  const { viewRfiMachineState, viewRfiMachineService } = useViewRfiMachine();
  const { translate } = useTranslator(['formLabels', 'buttons', 'rfiPage']);

  const impactCategories: ImpactCategories = {
    NONE: translate('rfiPage:none'),
    COST: translate('rfiPage:cost'),
    SCHEDULE: translate('rfiPage:schedule'),
    COST_AND_SCHEDULE: translate('rfiPage:costAndSchedule'),
  };

  const { rfiRevision, userEmails } = viewRfiMachineState.context;
  const { rfi, attachments: rfiAttachments } = rfiRevision;
  const drawingAttachments = rfiAttachments.DRAWING;
  const fileAttachments = rfiAttachments.FILE;
  const studioAttachments = rfiAttachments.STUDIO;
  const driveAttachments = rfiAttachments.DRIVE;
  const integrationAttachments = rfiAttachments.INTEGRATION;
  const savedAttachments = rfiAttachments.SAVED;

  const impactCategoryOptions = Object.entries(impactCategories).map(
    ([value, label]) => ({ label, value })
  );

  const userOptions = userEmails
    .map((email: string) => ({ label: email, value: email }))
    .sortBy(({ label }) => label)
    .toArray();

  const sectionlabel = (
    <div className="row justify-between items-center w100">
      <div>{translate('rfiPage:officialQuestion')}</div>
      {rfi.id ? (
        <div className="b-2 px3 py1 font-size-4 regular">
          {translate('rfiPage:draft')}
        </div>
      ) : null}
    </div>
  );

  const sendUpdateRFIEvent = (rfi: RFI) => {
    viewRfiMachineService.send({
      type: ViewRFIEventsEnum.UPDATE_RFI,
      data: rfi,
    });
  };

  const handleUpdateAttachmment = (
    attachment: FileList | InsertFilesSubmitParams
  ) => {
    if (attachment instanceof FileList) {
      viewRfiMachineService.send({
        type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
        data: { item: attachment, source: AttachmentTypeEnum.FILE },
      });
    } else {
      viewRfiMachineService.send([
        {
          type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
          data: {
            item: attachment.projectDriveSelectedItems,
            source: AttachmentTypeEnum.DRIVE,
          },
        },
        {
          type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
          data: {
            item: attachment.integrationsSelectedItems,
            source: AttachmentTypeEnum.INTEGRATION,
          },
        },
        {
          type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
          data: {
            item: OrderedSet(attachment.selectedPages),
            source: AttachmentTypeEnum.DRAWING,
          },
        },
        {
          type: ViewRFIEventsEnum.UPDATE_ATTACHMENT,
          data: {
            item: attachment.studioItems,
            meta: attachment.studioProjectData,
            source: AttachmentTypeEnum.STUDIO,
          },
        },
      ]);
    }
  };

  const handleRemoveAttachmment = (
    attachment:
      | RFIAttachment
      | File
      | string
      | ProjectDriveSelectedItem
      | IntegrationsSelectedItem
      | StudioProjectFileData,
    source: AttachmentTypeEnum
  ) => {
    viewRfiMachineService.send({
      type: ViewRFIEventsEnum.REMOVE_ATTACHMENT,
      data: {
        item: attachment,
        source,
      },
    });
  };

  return (
    <OverlaySection label={sectionlabel}>
      <div className="mb3">
        <div className="medium font-size-7 black-2">
          RFI {rfi.number}
          {rfi.versionnumber ? `.${rfi.versionnumber}` : null}
        </div>
      </div>
      <div className="mb3">
        <Label label={translate('formLabels:subject')} required>
          <div className="light row justify-end mr3">
            {rfi.title.length}/200
          </div>
        </Label>
        <BaseInput
          id="create-rfi-overlay-subject-input"
          className="w100"
          defaultValue={rfi.title}
          maxLength={200}
          onChange={({
            target: { value },
          }: React.ChangeEvent<HTMLInputElement>): void => {
            const newrfi = rfi.set('title', value);
            sendUpdateRFIEvent(newrfi);
          }}
        />
      </div>
      <div className="mb3">
        <Label label={translate('formLabels:question')} required />
        <BaseTextarea
          id="create-rfi-overlay-question-textarea"
          className="w100"
          defaultValue={rfi.question}
          onChange={({
            target: { value },
          }: React.ChangeEvent<HTMLTextAreaElement>): void => {
            const newrfi = rfi.set('question', value);
            sendUpdateRFIEvent(newrfi);
          }}
        />
      </div>
      <div className="mb3">
        <Label label={translate('formLabels:assignee')} required />
        <SingleSelect
          id="create-rfi-overlay-assignee-select"
          options={userOptions}
          value={
            rfi.assigneeEmail
              ? {
                  label: rfi.assigneeEmail,
                  value: rfi.assigneeEmail,
                }
              : null
          }
          onChange={(selectedAssignee): void => {
            const newrfi = rfi.set('assigneeEmail', selectedAssignee.value);
            sendUpdateRFIEvent(newrfi);
          }}
        />
      </div>
      <div className="mb3">
        <Label label={translate('formLabels:distroList')} />
        <OrderedSetMultiSelect
          id="create-rfi-overlay-distro-list-select"
          options={userEmails}
          values={rfi.distributionlist}
          placeholder={translate('formLabels:selectWithDots')}
          onChange={(nextValues): void => {
            const newrfi = rfi.set('distributionlist', nextValues);
            sendUpdateRFIEvent(newrfi);
          }}
        />
      </div>
      <div className="mb3">
        <Label label={translate('formLabels:duedate')} />
        <div className="row items-center">
          <DateDropdown
            id="create-rfi-overlay-duedate"
            date={rfi.duedate}
            minDate={new Date()}
            onChange={(date: Date): void => {
              const newrfi = rfi.set(
                'duedate',
                Math.floor(date?.getTime() / 1000) || 0
              );
              sendUpdateRFIEvent(newrfi);
            }}
          />
        </div>
      </div>
      <div className="mb3">
        <Label label={translate('formLabels:impact')} />
        <SingleSelect
          id="create-rfi-overlay-impact-select"
          options={impactCategoryOptions}
          value={{
            label: impactCategories[rfi.impactcategory.kind],
            value: rfi.impactcategory.kind,
          }}
          onChange={(nextImpactCategory: SelectOption) => {
            const newrfi = rfi.set(
              'impactcategory',
              new ImpactCategory({
                kind: nextImpactCategory.value as RFIImpactCategoryKind,
                display: nextImpactCategory.label,
              })
            );
            sendUpdateRFIEvent(newrfi);
          }}
        />
      </div>
      <RFIFilesList
        files={fileAttachments}
        sheetVersions={rfiAttachments.drawingSheetVersions}
        attachments={savedAttachments}
        projectDriveItems={driveAttachments}
        integrationItems={integrationAttachments}
        studioItems={studioAttachments}
        onAttachmentRemove={(attachment, source) =>
          handleRemoveAttachmment(attachment, source)
        }
      />
      <div className="w100 row justify-between">
        <InsertFilesButton
          id="forward-rfi-overlay-attach-drawings-btn"
          initialPages={drawingAttachments}
          initialProjectDriveItems={driveAttachments}
          initialIntegrationItems={integrationAttachments}
          initialStudioItems={studioAttachments}
          onSubmit={({
            selectedPages,
            projectDriveSelectedItems,
            integrationsSelectedItems,
            studioItems,
            studioProjectData,
          }) => {
            handleUpdateAttachmment({
              selectedPages,
              projectDriveSelectedItems,
              integrationsSelectedItems,
              studioItems,
              studioProjectData,
            });
            if (isPendoEnabled && projectDriveSelectedItems.count() > 0) {
              pendo.track('Added RFI Attachment from Integrations');
            }
            if (isPendoEnabled && integrationsSelectedItems.count() > 0) {
              pendo.track('Added RFI Attachment from Account Integrations');
            }
          }}
          className="grow-1 ws2"
        />
        <div className="ws1" />
        <FileUpload
          id="forward-rfi-overlay-attach-document"
          accept=""
          onFileSelect={(addFiles): void => {
            handleUpdateAttachmment(addFiles);
          }}
        >
          {({ openFilePicker }): React.ReactNode => (
            <IconButton
              variant="secondary"
              isLoading={false}
              size="large"
              onClick={openFilePicker}
              className="flex-center grow-1 ws2"
            >
              <Upload />
              {translate('buttons:uploadFiles')}
            </IconButton>
          )}
        </FileUpload>
      </div>
    </OverlaySection>
  );
};

export default OfficialQuestionSection;
